/* eslint-disable max-lines-per-function */
import dayjs from 'dayjs'
import type { DeviceDetails, DevicePatrolman, ModalField } from '../interfaces'
import {
  isConnectDevice,
  isDisconnectDevice,
  isBackToService,
  isReplaceDevice,
  isDeviceTransfer,
  isCrewComposition,
  isCreateDevice,
  formatUserLabel,
  isEditIdentification,
  isEditImei,
  isSetOnMission,
  isActivateDevice,
  isBackToAvailable,
  isLastDispatches,
  isOutOfService,
  extractLastDispatchCodes,
  extractReplaceDeviceNames,
  buildBaseUserFields,
  buildDeviceNameField,
} from './modalFieldHelpers'

export function getModalFields(selectedRow: unknown, translate: (key: string) => string): readonly ModalField[] {
  if (!selectedRow) return []

  const details = selectedRow as DeviceDetails

  // novo helper inline: extrai composição da guarnição do novo campo crew_composition
  function buildCrewComposition(details: DeviceDetails) {
    // novo formato: details.crew_composition: [{ representation_name, position, registration }]
    if (Array.isArray((details as any).crew_composition) && (details as any).crew_composition.length) {
      return (details as any).crew_composition
        .map((member: any) => {
          const name = member.representation_name ?? member.name ?? member.login ?? String(member.id ?? '')
          const registration = member.registration
          const position = member.position ?? ''
          
          // Formato: [registration] name ou apenas name
          const displayName = registration ? `[${registration}] ${name}` : name
          
          return position ? `${position}: ${displayName}` : displayName
        })
        .filter(Boolean)
    }

    // fallback para formato antigo (commander/driver/patrolmans)
    const commanderName = formatUserLabel(details.commander)
    const driverName = formatUserLabel(details.driver)
    const patrolmansText = Array.isArray(details.patrolmans) && details.patrolmans.length > 0
      ? details.patrolmans.map((person: DevicePatrolman) => formatUserLabel(person) ?? person.login ?? String(person.id)).join(', ')
      : null

    return [
      commanderName ? `Comandante: ${commanderName}` : null,
      driverName ? `Motorista: ${driverName}` : null,
      patrolmansText ? `Patrulhantes: ${patrolmansText}` : null,
    ].filter(Boolean)
  }

  const connect = isConnectDevice(details)
  const disconnect = isDisconnectDevice(details)
  const replaceDevice = isReplaceDevice(details)
  const deviceTransfer = isDeviceTransfer(details)
  const crewComposition = isCrewComposition(details)
  const creatingDevice = isCreateDevice(details)
  const editIdentification = isEditIdentification(details)
  const editImei = isEditImei(details)
  const setOnMission = isSetOnMission(details)
  const activateDevice = isActivateDevice(details)
  const outOfService = isOutOfService(details)
  const backToService = isBackToService(details)
  const backToAvailable = isBackToAvailable(details)
  const lastDispatches = isLastDispatches(details)


  if (connect) {
    const composition = buildCrewComposition(details)

    return [
      ...buildBaseUserFields(details, translate),
      { label: 'Nome do Dispositivo', value: details.device?.name ?? 'Não informado' },
      { label: 'KM inicial', value: details.km_initial ?? 'Não informado' },
      { label: 'Atuação', value: details.task ?? 'Não informado' },
      { label: 'Composição da Guarnição', value: composition.length > 0 ? composition.join(' | ') : 'Não informado', size: 6 },
      { label: 'Observações', value: details.notes ?? 'Não informado', size: 6 },
    ]
  }

  if (disconnect) return [...buildBaseUserFields(details, translate), buildDeviceNameField(details)]

  if (replaceDevice) {
    // use helper to extract origin/destination device names
    const names = extractReplaceDeviceNames(details)
    return [
      ...buildBaseUserFields(details, translate),
      { label: 'Dispositivo de Origem', value: names.origin ?? 'Não informado' },
      { label: 'Dispositivo de Destino', value: names.destination ?? 'Não informado' },
    ]
  }

  if (deviceTransfer) {
    return [
      ...buildBaseUserFields(details, translate),
      buildDeviceNameField(details),
      { label: 'Grupo de Despacho', value: details.dest_dispatch_group?.name ?? details.dest_dispatch_group?.acronym ?? 'Não informado' },
    ]
  }

  if (creatingDevice) {
    return [
      { label: 'Nome do Dispositivo', value: details.device?.name ?? 'Não informado' },
      { label: 'Marca', value: details.device?.brand ?? 'Não informado' },
      { label: 'Modelo', value: details.device?.model ?? 'Não informado' },
      { label: 'Tipo de Dispositivo', value: details.device_type?.name ?? 'Não informado' },
      { label: 'Unidade Operacional', value: details.operation_unit?.name ?? 'Não informado' },
      { label: 'Agência', value: details.agency?.name ?? 'Não informado' },
      { label: 'Grupo de Despacho', value: details.dispatch_group?.name ?? details.dispatch_group?.acronym ?? 'Não informado' },
      { label: 'IMEI do Dispositivo', value: details.device?.imei ?? 'Não informado' },
      { label: 'Cód. Rastreio', value: details.tracking_id ?? details['tracking_id'] ?? 'Não informado' },
      { label: 'Bloqueado Rastreio Temporário', value: details.lock_temporary_tracking ? 'Sim' : 'Não' },
      { label: 'Realizado em', value: details.created_at ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm') : 'Não informado' },
    ]
  }

  if (crewComposition) {
    const composition = buildCrewComposition(details)

    return [
      ...buildBaseUserFields(details, translate),
      buildDeviceNameField(details),
      { label: 'Composição da Guarnição', value: composition.length > 0 ? composition.join(' | ') : 'Não informado', size: 6 },
    ]
  }

  if (editImei) {
    const baseFields = buildBaseUserFields(details, translate)
    const userField = baseFields[0]
    const actionField = baseFields[1]
    const originField = baseFields[2]
    const performedAtField = { label: translate('Performed at') || 'Realizado em', value: details.created_at ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm') : 'Não informado' }

    return [
      userField,
      actionField,
      originField,
      { label: translate('Device:name') || 'Nome', value: details.device?.name ?? 'Não informado' },
      { label: translate('Brand') || 'Marca', value: details.device?.brand ?? 'Não informado' },
      { label: translate('Model') || 'Modelo', value: details.device?.model ?? 'Não informado' },
      {
        label: translate('Device Type') || 'Tipo de Dispositivo',
        value: details.device_type?.name ?? details.device?.device_type?.name ?? 'Não informado',
      },
      {
        label: translate('Operation unit') || 'Unidade Operacional',
        value: details.operation_unit?.name ?? details.device?.operation_unit?.name ?? 'Não informado',
      },
      { label: translate('IMEI') || 'IMEI', value: details.device?.imei ?? 'Não informado' },
      {
        label: translate('Lock_temporary_tracking') || 'Bloqueado Rastreamento Temporário',
        value: (details.lock_temporary_tracking ?? details.device?.lock_temporary_tracking) ? 'Sim' : 'Não',
      },
      performedAtField,
    ]
  }

  if (editIdentification) {
    const baseFields = buildBaseUserFields(details, translate)

    // baseFields layout: [User, Action, Origin, (maybe Station), PerformedAt]
    const userField = baseFields[0]
    const actionField = baseFields[1]
    const originField = baseFields[2]
    // Performed at (prefer using created_at directly to avoid index coupling)
    const performedAtField = { label: translate('Performed at') || 'Realizado em', value: details.created_at ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm') : 'Não informado' }

    return [
      userField,
      actionField,
      originField,
      { label: translate('Device:name') || 'Nome', value: details.device?.name ?? 'Não informado' },
      { label: translate('Brand') || 'Marca', value: details.device?.brand ?? 'Não informado' },
      { label: translate('Model') || 'Modelo', value: details.device?.model ?? 'Não informado' },
      {
        label: translate('Device Type') || 'Tipo de Dispositivo',
        value: details.device_type?.name ?? details.device?.device_type?.name ?? 'Não informado',
      },
      {
        label: translate('Operation unit') || 'Unidade Operacional',
        value: details.operation_unit?.name ?? details.device?.operation_unit?.name ?? 'Não informado',
      },
      {
        label: translate('Agency:name') || 'Agência',
        value: details.agency?.name ?? details.device?.agency?.name ?? 'Não informado',
      },
      {
        label: translate('Dispatch group') || 'Grupo de Despacho',
        value: details.dispatch_group?.name ?? details.dispatch_group?.acronym ?? details.device?.dispatch_group?.name ?? details.device?.dispatch_group?.acronym ?? 'Não informado',
      },
      { label: translate('IMEI') || 'IMEI', value: details.device?.imei ?? 'Não informado' },
      {
        label: translate('Tracking code') || 'Cód. Rastreamento',
        value: details.tracking_id ?? details['tracking_id'] ?? details.device?.tracking_id ?? 'Não informado',
      },
      // Bloqueado Rastreamento Temporário
      {
        label: translate('Lock_temporary_tracking') || 'Bloqueado Rastreamento Temporário',
        value: (details.lock_temporary_tracking ?? details.device?.lock_temporary_tracking) ? 'Sim' : 'Não',
      },
      // Realizado em
      performedAtField,
    ]
  }

  if (setOnMission) {
    return [
      ...buildBaseUserFields(details, translate),
      buildDeviceNameField(details),
      { label: 'Motivo em missão', value: details.on_mission_reason?.name ?? 'Não informado' },
      { label: 'Comentário', value: details.comment ?? 'Não informado',size: 6 },
    ]
  }

  if (activateDevice) return [...buildBaseUserFields(details, translate), buildDeviceNameField(details)]

  if (outOfService) {
    return [
      ...buildBaseUserFields(details, translate),
      buildDeviceNameField(details),
      { label: 'Motivo Fora de Serviço', value: details.out_of_service_reason?.name ?? details['out_of_service_reason']?.name ?? 'Não informado' },
      { label: 'Comentário', value: details.comment ?? 'Não informado' },
    ]
  }

  if (backToService) return [...buildBaseUserFields(details, translate), { label: 'Nome do Dispositivo', value: details.action_device?.name ?? details.device?.name ?? 'Não informado' }]

  if (backToAvailable) return [...buildBaseUserFields(details, translate), buildDeviceNameField(details)]

  if (lastDispatches) {
    const dispatchCodes = extractLastDispatchCodes(details)
    return [
      ...buildBaseUserFields(details, translate),
      buildDeviceNameField(details),
      { label: 'Últimos Despachos Visualizados', value: dispatchCodes.length ? dispatchCodes.join(' • ') : 'Não informado', size: 6 },
    ]
  }

  return [
    { label: 'Comentário', value: details.comment ?? 'Não informado' },
    { label: 'Nome do Dispositivo', value: details.device?.name ?? 'Não informado' },
    { label: 'Marca do Dispositivo', value: details.device?.brand ?? 'Não informado' },
    { label: 'Modelo do Dispositivo', value: details.device?.model ?? 'Não informado' },
    { label: 'IMEI do Dispositivo', value: details.device?.imei ?? 'Não informado' },
    { label: 'Motivo da Missão', value: details.on_mission_reason?.name ?? 'Não informado' },
  ]
}

export default getModalFields
